"use client";

import Link from "next/link";
import { Box, Button, Heading, Stack, Text } from "@chakra-ui/react";
import type { ReactElement } from "react";
import { Logo } from "@/components/ui/logo";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}): ReactElement {
  return (
    <Box
      minH="100dvh"
      bg="var(--color-background)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={{ base: 6, md: 8 }}
      py={{ base: 12, md: 16 }}
    >
      <Stack gap={{ base: 6, md: 8 }} maxW="520px" w="full" textAlign="center" alignItems="center">
        <Link href="/" aria-label="OTAlytics home">
          <Logo withText />
        </Link>

        <Stack gap={3}>
          <Text
            fontSize="13px"
            fontWeight="600"
            color="var(--color-error)"
            letterSpacing="var(--letter-spacing-label)"
            textTransform="uppercase"
          >
            Something went wrong
          </Text>
          <Heading
            as="h1"
            fontSize={{ base: "28px", md: "36px" }}
            fontWeight="600"
            color="var(--color-text-primary)"
            letterSpacing="var(--letter-spacing-tight)"
          >
            This page could not be loaded
          </Heading>
          <Text fontSize={{ base: "15px", md: "16px" }} color="var(--color-text-secondary)">
            The problem is on our side, not yours. Your data and tracking are
            unaffected. Try again, and if it keeps happening come back in a few
            minutes.
          </Text>
        </Stack>

        <Stack
          direction={{ base: "column", sm: "row" }}
          gap={3}
          w={{ base: "full", sm: "auto" }}
          justifyContent="center"
        >
          <Button
            onClick={reset}
            bg="var(--color-primary)"
            color="white"
            fontWeight="500"
            fontSize="14px"
            borderRadius="var(--radius-sm)"
            size="lg"
            px={6}
            w={{ base: "full", sm: "auto" }}
            transition="var(--transition-fast)"
            _hover={{ bg: "var(--color-primary-hover)" }}
          >
            Try again
          </Button>
          <Link href="/dashboard">
            <Button
              bg="var(--color-surface)"
              color="var(--color-text-primary)"
              border="0.5px solid var(--color-border)"
              fontWeight="500"
              fontSize="14px"
              borderRadius="var(--radius-sm)"
              size="lg"
              px={6}
              w={{ base: "full", sm: "auto" }}
              transition="var(--transition-fast)"
              _hover={{
                bg: "var(--color-surface-hover)",
                borderColor: "var(--color-border-strong)",
              }}
            >
              Go to dashboard
            </Button>
          </Link>
        </Stack>

        {error.digest && (
          <Text fontSize="12px" color="var(--color-text-muted)">
            Reference code: {error.digest}
          </Text>
        )}
      </Stack>
    </Box>
  );
}
